import {t} from '@lingui/macro';
import {isUndefined, minBy} from 'lodash';
import React, {Component, Ref} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {findScrollParent} from '../../../../../../libs/shared/helpers/src/browser/domHelpers';
import {
  Handler,
  HandlerOf,
  KeyboardEventHandler,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {Highlighter} from '../../../../common/highlighter';

/*
 * Constants.
 */

const searchFocusedAttribute = 'data-search-focused';

/*
 * Props.
 */

interface ConversationSearchBarProps {
  onCloseClick: Handler;
  inputRef?: Ref<BoxedInput>;
}

/*
 * Style.
 */

const StyledSearchDiv = styled.div`
  width: 210px;
`;

const StyledAccessoriesDiv = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
`;

const StyledCounterDiv = styled.span`
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  margin-right: 8px;
  line-height: 20px;
`;

const StyledControlsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 1px;

  margin: 4px;
  border-radius: 3px;
  overflow: hidden;
`;

const StyledControlButton = styled(ChromelessButton)`
  padding: 2px;
  background-color: ${(p) => p.theme.alphas.black20};
  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    background: ${(p) => p.theme.alphas.black40};
  }
`;

const StyledMainHighlighter = styled(Highlighter)`
  background-color: ${(p) => p.theme.palette.orange.shade30};
  color: ${(p) => p.theme.greys.shade90};

  &[${searchFocusedAttribute}] {
    background-color: ${(p) => p.theme.palette.orange.highlight};
  }
`;

/*
 * Component.
 */

interface ConversationSearchBarState {
  query: string;
  results?: ReadonlyArray<Element>;
  focusedResultIndex?: number;
}

class ConversationHeaderSearchBarComponent extends Component<
  ConversationSearchBarProps & InternationalizationContextProps,
  ConversationSearchBarState
> {
  constructor(props: ConversationSearchBarProps & InternationalizationContextProps) {
    super(props);
    this.state = {query: ''};
  }

  /*
   * Lifecycle.
   */

  componentDidUpdate(_previousProps: unknown, previousState: Readonly<ConversationSearchBarState>) {
    // The results or selected index didn't change, nothing to do.
    const {results, focusedResultIndex} = this.state;
    if (results === previousState.results && focusedResultIndex === previousState.focusedResultIndex) {
      return;
    }

    // Unfocus the previously focused element, if any.
    clearFocused(previousState);

    // Find the element to focus.
    const target = results && !isUndefined(focusedResultIndex) && results[focusedResultIndex];
    if (!target) {
      return;
    }

    target.scrollIntoView({behavior: 'auto', block: 'nearest'});
    target.setAttribute(searchFocusedAttribute, 'true');
  }

  componentWillUnmount() {
    clearFocused(this.state);
  }

  /*
   * Event handlers.
   */

  /** Perform the search on each key stroke. */
  private readonly onChange: HandlerOf<string> = (query) => {
    this.setState({query});
  };

  /** When results are found, update the state. */
  private readonly onResults: HandlerOf<ReadonlyArray<Element>> = (results) => {
    const firstVisibleResult = findFirstVisibleElement(results);
    const focusedResultIndex = !isUndefined(firstVisibleResult)
      ? results.indexOf(firstVisibleResult)
      : undefined;
    this.setState({results, focusedResultIndex});
  };

  /** Focus the previous/next result on Enter. */
  private readonly onKeyDown: KeyboardEventHandler = (event) => {
    // We only want enter and up/down arrow keys
    if (event.key !== Key.Enter && event.key !== Key.ArrowDown && event.key !== Key.ArrowUp) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if ((event.shiftKey && event.key === Key.Enter) || event.key === Key.ArrowUp) {
      this.goToPrevious();
      return;
    }

    this.goToNext();
  };

  /** Focus the next result. */
  private readonly goToPrevious = () => {
    const {results, focusedResultIndex} = this.state;
    if (!results || isUndefined(focusedResultIndex)) {
      return;
    }

    const isMinimum = focusedResultIndex <= 0;
    const newFocusedResultIndex = isMinimum ? results.length - 1 : focusedResultIndex - 1;

    this.setState({focusedResultIndex: newFocusedResultIndex});
  };

  // Focus the previous result.
  private readonly goToNext = () => {
    const {results, focusedResultIndex} = this.state;
    if (!results || isUndefined(focusedResultIndex)) {
      return;
    }

    const isMaximum = focusedResultIndex >= results.length - 1;
    const newFocusedResultIndex = isMaximum ? 0 : focusedResultIndex + 1;

    this.setState({focusedResultIndex: newFocusedResultIndex});
  };

  /*
   * Render.
   */

  render() {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledSearchDiv>
            <BoxedInput ref={this.props.inputRef} backgroundColor={theme.greys.shade30} shouldAutoFocus>
              {(childProps) => this.renderInputContent(childProps)}
            </BoxedInput>
            <StyledMainHighlighter query={this.state.query} onResults={this.onResults} usePartialMatch />
          </StyledSearchDiv>
        )}
      </UserThemeConsumer>
    );
  }

  private renderInputContent(childProps: BoxedInputChildProps) {
    const {localize, onCloseClick} = this.props;
    return (
      <>
        <BoxedInputElement
          {...childProps}
          type={BoxedInputElementTypesEnum.TEXT}
          placeholder={localize(t('conversation-header-search')`Find`)}
          value={this.state.query}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
        />
        <StyledAccessoriesDiv>
          {this.maybeRenderResultCounter()}
          <StyledControlsDiv>
            <StyledControlButton onClick={this.goToPrevious}>
              <Icon name="chevronUpSmall" />
            </StyledControlButton>
            <StyledControlButton onClick={this.goToNext}>
              <Icon name="chevronDownSmall" />
            </StyledControlButton>
            <StyledControlButton onClick={onCloseClick}>
              <Icon name="exitSmall" />
            </StyledControlButton>
          </StyledControlsDiv>
        </StyledAccessoriesDiv>
      </>
    );
  }

  private maybeRenderResultCounter() {
    // Check if we need to display a counter.
    const {query, results, focusedResultIndex} = this.state;
    if (!query || !results || isUndefined(focusedResultIndex)) {
      return null;
    }

    return (
      <StyledCounterDiv>
        {focusedResultIndex + 1}/{results.length}
      </StyledCounterDiv>
    );
  }
}

export const ConversationHeaderSearchBar = withInternationalization(ConversationHeaderSearchBarComponent);

/*
 * Helpers.
 */

/** Unfocus the focused element, if any. */
function clearFocused(state: ConversationSearchBarState) {
  const {results, focusedResultIndex} = state;
  const focusedElement = results && !isUndefined(focusedResultIndex) && results[focusedResultIndex];
  if (!focusedElement) {
    return;
  }

  focusedElement.removeAttribute(searchFocusedAttribute);
}

function findFirstVisibleElement(elements: ReadonlyArray<Element>) {
  // If there are no elements, nothing to do.
  if (elements.length === 0) {
    return undefined;
  }

  // Otherwise find the bounding rect for the scroll parent.
  const scrollParent = findScrollParent(elements[0]);
  const parentRect = scrollParent && scrollParent.getBoundingClientRect();
  if (!parentRect) {
    return elements[0];
  }

  // Find the closest visible element.
  return minBy(elements, (element) => {
    const elementRect = element.getBoundingClientRect();

    // If the element is after, return the normal offset.
    if (elementRect.top > parentRect.top) {
      return elementRect.top;
    }

    // Otherwise, sort by closest above.
    return Number.MAX_SAFE_INTEGER - elementRect.top;
  });
}
